import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { prop } from '@ipfabric/utils/fp/prop';
import {
  type GlobalStateDispatch,
  useGlobalStateContextSelectors,
} from 'components/GlobalState/GlobalStateModel';
import { refreshTokens } from 'components/GlobalState/commands/refreshTokens';
import { refreshTokenExpirationLocalStorage } from 'components/GlobalState/refreshTokenExpirationLocalStorage';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type AbortableResult, abortable } from 'utils/abortRequest/abortable';
import type {
  AbortableApiRequestParams,
  ApiRequestParams,
} from './ApiRequestParams';
import { cachedQueryDefaultOptions } from './queryOptions';
import { request as apiRequest } from './request';
import { isRequestProlongingToken } from './requestProlongingToken';

export type ApiRequestFunction = <TResult = any, TRequestData = any>(
  request: ApiRequestParams<TRequestData>,
) => Promise<TResult>;

type QueryMethodResult<TResult = any, TRequestData = any> = {
  queryFn: () => Promise<TResult>;
  queryKey: [ApiRequestParams<TRequestData>];
};

type CachedQueryMethodResult<
  TResult = any,
  TRequestData = any,
> = QueryMethodResult<TResult, TRequestData> & {
  staleTime: number;
  refetchOnReconnect: boolean;
  refetchOnWindowFocus: boolean;
};

export interface ApiHook {
  /**
   * Call a request that can be aborted with AbortSignal
   *
   * Aborted request is considered an expected state that should be handled
   * and it is difficult to determine sane default behavior.
   * Set loading it to initial state (nothing loaded), loaded (previous
   * content is kept in place) or don't intercept it at all (another running request
   * replaced this one and is already handling loading state on its own)?
   * Proceed in running operation with following commands or not?
   * Therefore we decided to return an optional structure to enforce users handle
   * this manually.
   *
   * We decided to use AbortController API, which looks as a workaround how abortion
   * was implemented into existing fetch() api keeping it backward compatible,
   * but it has shown to be useful exactly this way - pass an object controlling abortion
   * from above. For example these cases:
   *
   * 1. using an async closure in useEffect and aborting the request in its destructor,
   * 2. having a "singleton request" controller globally maintained by component.
   *
   * See also singleton controllers for another smart usage of request abortion.
   *
   * @see createSingletonController
   * @see createSingletonControllerCollection
   * @example
   * // basic usage
   * const api = useApi();
   * const abortController = new AbortController();
   * const result = await api.abortableRequest({
   *   url: '...',
   *   abortSignal: abortController.signal,
   * });
   * setTimeout(() => {
   *   abortController.abort(); // abort the request if not resolved in 1000ms
   * }, 1000);
   * if (result.aborted) {
   *   // in this case it is enough to just abort the function
   *   return;
   * }
   * // proceed with data
   * console.log(result.data);
   *
   * // usage in hooks with async/await closure aborted on unmount
   * useEffect(() => {
   *   const abortController = new AbortController();
   *   (async () => {
   *     const result = await api.abortableRequest({
   *       url: '...',
   *       abortSignal: abortController.signal,
   *     });
   *     // ... process the result
   *   })();
   *   return () => {
   *     abortController.abort();
   *   };
   * }, []);
   */
  abortableRequest: <TResult = any, TRequestData = any>(
    params: AbortableApiRequestParams<TRequestData>,
  ) => Promise<AbortableResult<TResult>>;

  /**
   * Call normal API request that naively expects response.
   *
   * Catch potential errors with try/catch if you need more than
   * default error handling.
   *
   * @example
   * const api = useApi();
   * const data = await api.request({url: '...'});
   */
  request: <TResult = any, TRequestData = any>(
    params: ApiRequestParams<TRequestData>,
  ) => Promise<TResult>;

  /**
   * Create a query method that can be used with react-query.
   *
   * @example
   * const api = useApi();
   * const {data, loading} = useQuery(api.query({
   *   method: 'GET',
   *   url: '...',
   * }));
   */
  query: <TResult = any, TRequestData = any>(
    params: ApiRequestParams<TRequestData>,
  ) => QueryMethodResult<TResult, TRequestData>;

  /**
   * Same as query, but sets react-query caching directives to far less aggressive refetching
   *
   * Useful for snapshot data, as they are static.
   * !! Use only for such data, don't cache anything else without careful consideration!
   *
   * @see query
   */
  cachedQuery: <TResult = any, TRequestData = any>(
    params: ApiRequestParams<TRequestData>,
  ) => CachedQueryMethodResult<TResult, TRequestData>;
}

/**
 * Business logic of calling API requests with authorization, error
 * handling etc. is inside a saga `app/utils/requests/request`.
 * This hook wraps a redux call to this saga, so you can call API requests outside sagas.
 *
 * Use this raw hook only if you need to call API requests outside GlobalState context.
 * By default, use `useApi` hook.
 */
export const useApiWithoutGlobalState = (props: {
  globalDispatch: GlobalStateDispatch;
}): ApiHook => {
  const navigate = useNavigate();
  const request = useStableCallback(
    async <TRequestData = any, TResult = any>(
      params: ApiRequestParams<TRequestData>,
    ): Promise<TResult> => {
      try {
        const refreshStartedAt = Date.now();
        const result: TResult = await apiRequest(
          params,
          () => props.globalDispatch(refreshTokens),
          (url) => navigate(url),
        );
        if (isRequestProlongingToken(params, result)) {
          refreshTokenExpirationLocalStorage.setValue(
            refreshStartedAt + result.refreshTokenExpiresInSeconds * 1000,
          );
        }
        return result;
      } catch (error) {
        throw error ?? new Error('API request cancelled');
      }
    },
  );
  const query: <TResult = any, TRequestData = any>(
    params: ApiRequestParams<TRequestData>,
  ) => QueryMethodResult<TResult, TRequestData> = useStableCallback(
    (params) => ({
      queryFn: async () => request(params),
      queryKey: [params],
    }),
  );

  const cachedQuery: <TResult = any, TRequestData = any>(
    params: ApiRequestParams<TRequestData>,
  ) => CachedQueryMethodResult<TResult, TRequestData> = useStableCallback(
    (params) => ({
      ...query(params),
      ...cachedQueryDefaultOptions,
    }),
  );

  return useMemo(
    () => ({
      abortableRequest: abortable(request),
      request,
      query,
      cachedQuery,
    }),
    [],
  );
};

export const useApi = (): ApiHook => {
  const globalCtxProps = useGlobalStateContextSelectors({
    dispatch: prop('dispatch'),
  });
  return useApiWithoutGlobalState({ globalDispatch: globalCtxProps.dispatch });
};
